import type { Doctor, Praxis, Recipient, Patient, Invoice, Exam } from "src/types";
import type { FinApiStatus } from "src/hooks/useFinApi";

// ── Auth ─────────────────────────────────────────────────────────────────────

export const DEMO_TOKEN = "demo-token-medinvoice";

export const DEMO_DOCTOR: Doctor = {
    id: "demo-doctor-1",
    firstName: "Andreas",
    lastName: "Müller",
    salutation: "H",
    title: "Dr. med.",
    email: "a.mueller@demo-praxis.de",
    userRights: 3,
    lanr: "123456700",
    specialty: ["Innere Medizin"],
    iban: "DE89370400440532013000",
    bic: "COBADEFFXXX",
    taxNumber: "123/456/78901",
    street: "Musterstraße 12",
    plz: "80331",
    city: "München",
};

export const DEMO_PRAXIS: Praxis = {
    id: "demo-praxis-1",
    name: "Praxis Dr. Müller",
    street: "Musterstraße 12",
    plz: "80331",
    city: "München",
    email: "info@demo-praxis.de",
    iban: "DE89370400440532013000",
    bic: "COBADEFFXXX",
    bank: "Commerzbank",
    specialization: ["Innere Medizin"],
    businessPlan: 3,
    subscriptionStatus: "active",
    createdAt: "2025-01-15T10:00:00.000Z",
};

// ── Date helpers ──────────────────────────────────────────────────────────────

function demoDate(monthsAgo: number, day: number, hour = 9): string {
    const d = new Date();
    d.setDate(day);
    d.setHours(hour, 0, 0, 0);
    d.setMonth(d.getMonth() - monthsAgo);
    return d.toISOString();
}

function demoDue(monthsAgo: number, day: number): string {
    return demoDate(Math.max(0, monthsAgo - 1), day);
}

// ── Recipients (6 — varied scenarios) ────────────────────────────────────────
// rec-1, rec-3, rec-5: Patient = Recipient (same person)
// rec-2, rec-4, rec-6: Recipient ≠ Patient (billing for family member)

export const DEMO_RECIPIENTS: Recipient[] = [
    {
        // COMPLETE — patient = recipient — portal account
        id: "demo-rec-1",
        firstName: "Heinrich",
        lastName: "Wagner",
        salutation: "H",
        title: "Prof. Dr.",
        email: "h.wagner@example.com",
        tel: ["+49 89 111 2233", "+49 176 100 2000"],
        birthDate: "1958-03-12",
        street: "Leopoldstraße",
        number: 45,
        plz: "80802",
        city: "München",
        hasPortalAccount: true,
        questionnaireStatus: "submitted",
        invoicesDone: 2,
        invoicesOpen: 1,
        invoicesNotSent: 0,
    },
    {
        // COMPLETE — recipient ≠ patient (mother billing for son)
        id: "demo-rec-2",
        firstName: "Brigitte",
        lastName: "Schmidt",
        salutation: "F",
        email: "b.schmidt@example.com",
        tel: ["+49 89 222 3344"],
        birthDate: "1955-06-20",
        street: "Schillerstraße",
        number: 8,
        plz: "80336",
        city: "München",
        hasPortalAccount: false,
        questionnaireStatus: "submitted",
        invoicesDone: 1,
        invoicesOpen: 1,
        invoicesNotSent: 0,
    },
    {
        // NO EMAIL — patient = recipient
        id: "demo-rec-3",
        firstName: "Maria",
        lastName: "Hoffmann",
        salutation: "F",
        tel: ["+49 89 333 4455"],
        birthDate: "1972-07-24",
        street: "Maximilianstraße",
        number: 22,
        plz: "80539",
        city: "München",
        hasPortalAccount: false,
        questionnaireStatus: "not_sent",
        invoicesDone: 0,
        invoicesOpen: 0,
        invoicesNotSent: 1,
    },
    {
        // COMPLETE — recipient ≠ patient (husband billing for wife) — portal account
        id: "demo-rec-4",
        firstName: "Thomas",
        lastName: "Meier",
        salutation: "H",
        title: "Dr.",
        email: "t.meier@example.com",
        tel: ["+49 89 444 5566"],
        birthDate: "1968-11-05",
        street: "Brienner Straße",
        number: 30,
        plz: "80333",
        city: "München",
        hasPortalAccount: true,
        questionnaireStatus: "sent",
        invoicesDone: 0,
        invoicesOpen: 1,
        invoicesNotSent: 1,
    },
    {
        // INCOMPLETE (no street) — patient = recipient
        id: "demo-rec-5",
        firstName: "Petra",
        lastName: "König",
        salutation: "F",
        email: "p.koenig@example.com",
        tel: ["+49 176 555 6677"],
        birthDate: "1985-04-30",
        plz: "80335",
        city: "München",
        hasPortalAccount: false,
        questionnaireStatus: "not_sent",
        invoicesDone: 0,
        invoicesOpen: 1,
        invoicesNotSent: 1,
    },
    {
        // COMPLETE — recipient ≠ patient (father billing for daughter)
        id: "demo-rec-6",
        firstName: "Hans-Jürgen",
        lastName: "Bauer",
        salutation: "H",
        email: "hj.bauer@example.com",
        tel: ["+49 89 666 7788"],
        birthDate: "1960-09-14",
        street: "Kaufingerstraße",
        number: 12,
        plz: "80331",
        city: "München",
        hasPortalAccount: false,
        questionnaireStatus: "not_sent",
        invoicesDone: 0,
        invoicesOpen: 1,
        invoicesNotSent: 0,
    },
];

// ── Patients (6 + 1 extra for multi-patient dropdown demo) ───────────────────

export const DEMO_PATIENTS: Patient[] = [
    {
        // Same person as rec-1 (patient = recipient)
        id: "demo-pat-1",
        firstName: "Heinrich",
        lastName: "Wagner",
        salutation: "H",
        title: "Prof. Dr.",
        email: "h.wagner@example.com",
        birthDate: "1958-03-12",
        gender: "M",
        street: "Leopoldstraße",
        number: 45,
        plz: "80802",
        city: "München",
        recipient: DEMO_RECIPIENTS[0],
        invoicesDone: 2,
        invoicesOpen: 1,
    },
    {
        // Son of rec-2 (Brigitte Schmidt) — recipient ≠ patient
        id: "demo-pat-2",
        firstName: "Maximilian",
        lastName: "Schmidt",
        salutation: "H",
        birthDate: "1985-02-14",
        gender: "M",
        street: "Schillerstraße",
        number: 8,
        plz: "80336",
        city: "München",
        recipient: DEMO_RECIPIENTS[1],
        invoicesDone: 1,
        invoicesOpen: 1,
    },
    {
        // Second son of rec-2 — extra patient for multi-patient dropdown demo
        id: "demo-pat-2b",
        firstName: "Tobias",
        lastName: "Schmidt",
        salutation: "H",
        birthDate: "1988-09-03",
        gender: "M",
        street: "Schillerstraße",
        number: 8,
        plz: "80336",
        city: "München",
        recipient: DEMO_RECIPIENTS[1],
        invoicesDone: 0,
        invoicesOpen: 0,
    },
    {
        // Same person as rec-3 (no email — patient = recipient)
        id: "demo-pat-3",
        firstName: "Maria",
        lastName: "Hoffmann",
        salutation: "F",
        birthDate: "1972-07-24",
        gender: "F",
        street: "Maximilianstraße",
        number: 22,
        plz: "80539",
        city: "München",
        recipient: DEMO_RECIPIENTS[2],
        invoicesDone: 0,
        invoicesOpen: 0,
        invoicesNotSent: 1,
    },
    {
        // Wife of rec-4 (Dr. Thomas Meier) — recipient ≠ patient
        id: "demo-pat-4",
        firstName: "Anna",
        lastName: "Meier",
        salutation: "F",
        birthDate: "1972-03-18",
        gender: "F",
        street: "Brienner Straße",
        number: 30,
        plz: "80333",
        city: "München",
        recipient: DEMO_RECIPIENTS[3],
        invoicesDone: 0,
        invoicesOpen: 1,
        invoicesNotSent: 1,
    },
    {
        // Same person as rec-5 (no street — patient = recipient)
        id: "demo-pat-5",
        firstName: "Petra",
        lastName: "König",
        salutation: "F",
        email: "p.koenig@example.com",
        birthDate: "1985-04-30",
        gender: "F",
        plz: "80335",
        city: "München",
        recipient: DEMO_RECIPIENTS[4],
        invoicesDone: 0,
        invoicesOpen: 1,
        invoicesNotSent: 1,
    },
    {
        // Daughter of rec-6 — recipient ≠ patient
        id: "demo-pat-6",
        firstName: "Lena",
        lastName: "Bauer",
        salutation: "F",
        birthDate: "1995-07-22",
        gender: "F",
        street: "Kaufingerstraße",
        number: 12,
        plz: "80331",
        city: "München",
        recipient: DEMO_RECIPIENTS[5],
        invoicesDone: 0,
        invoicesOpen: 1,
        invoicesNotSent: 0,
    },
];

// ── Invoices (10 total) ───────────────────────────────────────────────────────
// 6 Patient=Recipient: inv-1,3,5,7,9,10
// 4 Recipient≠Patient: inv-2,4,6,8
// 3 paid (auto-matched by bank): inv-1,2,3
// 5 unpaid with dunning: inv-4(M1),5(M1),6(M2),7(M2),8(M3)
// 2 not sent: inv-9,10

export const DEMO_INVOICES: Invoice[] = [
    {
        // PAID — auto bank match — P=R (Wagner)
        id: "demo-inv-1",
        readableId: "RE-2026-0001",
        status: 2,
        amount: 485.50,
        paidAmount: 485.50,
        dunning: 0,
        creationDate: demoDate(3, 5),
        sentDate: demoDate(3, 5, 10),
        dueDate: demoDue(3, 3),
        paidAt: demoDate(2, 20),
        paidMethod: "bank-auto",
        recipient: DEMO_RECIPIENTS[0],
        patient: DEMO_PATIENTS[0],
        source: "pad",
    },
    {
        // PAID — auto bank match — P≠R (Schmidt → Maximilian)
        id: "demo-inv-2",
        readableId: "RE-2026-0002",
        status: 2,
        amount: 312.80,
        paidAmount: 312.80,
        dunning: 0,
        creationDate: demoDate(3, 12),
        sentDate: demoDate(3, 12, 9),
        dueDate: demoDue(3, 10),
        paidAt: demoDate(2, 15),
        paidMethod: "bank-auto",
        recipient: DEMO_RECIPIENTS[1],
        patient: DEMO_PATIENTS[1],
        source: "pad",
    },
    {
        // PAID — auto bank match — P=R (Hoffmann, no email — sent by post)
        id: "demo-inv-3",
        readableId: "RE-2026-0003",
        status: 2,
        amount: 198.00,
        paidAmount: 198.00,
        dunning: 0,
        creationDate: demoDate(3, 18),
        sentDate: demoDate(3, 18, 14),
        dueDate: demoDue(3, 16),
        paidAt: demoDate(2, 10),
        paidMethod: "bank-manual",
        recipient: DEMO_RECIPIENTS[2],
        patient: DEMO_PATIENTS[3],
        source: "pad",
    },
    {
        // OVERDUE M1 — wrong bank ref, needs manual match — P≠R (Meier → Anna)
        id: "demo-inv-4",
        readableId: "RE-2026-0004",
        status: 4,
        amount: 754.20,
        paidAmount: 0,
        dunning: 1,
        creationDate: demoDate(2, 8),
        sentDate: demoDate(2, 8, 9),
        dueDate: demoDate(1, 6),
        recipient: DEMO_RECIPIENTS[3],
        patient: DEMO_PATIENTS[4],
        source: "pad",
    },
    {
        // OVERDUE M1 — P=R (König, incomplete address)
        id: "demo-inv-5",
        readableId: "RE-2026-0005",
        status: 4,
        amount: 230.00,
        paidAmount: 0,
        dunning: 1,
        creationDate: demoDate(2, 14),
        sentDate: demoDate(2, 14, 11),
        dueDate: demoDate(1, 12),
        recipient: DEMO_RECIPIENTS[4],
        patient: DEMO_PATIENTS[5],
        source: "manual",
    },
    {
        // OVERDUE M2 — P≠R (Bauer → Lena)
        id: "demo-inv-6",
        readableId: "RE-2026-0006",
        status: 4,
        amount: 920.00,
        paidAmount: 0,
        dunning: 2,
        creationDate: demoDate(3, 20),
        sentDate: demoDate(3, 20, 10),
        dueDate: demoDate(2, 18),
        recipient: DEMO_RECIPIENTS[5],
        patient: DEMO_PATIENTS[6],
        source: "pad",
    },
    {
        // OVERDUE M2 — wrong bank ref, needs manual match — P=R (Wagner again)
        id: "demo-inv-7",
        readableId: "RE-2026-0007",
        status: 4,
        amount: 445.60,
        paidAmount: 0,
        dunning: 2,
        creationDate: demoDate(3, 25),
        sentDate: demoDate(3, 25, 9),
        dueDate: demoDate(2, 23),
        recipient: DEMO_RECIPIENTS[0],
        patient: DEMO_PATIENTS[0],
        source: "pad",
    },
    {
        // OVERDUE M3 — has patient question — P≠R (Schmidt → Maximilian)
        id: "demo-inv-8",
        readableId: "RE-2026-0008",
        status: 4,
        amount: 560.40,
        paidAmount: 0,
        dunning: 3,
        amountMismatch: true,
        hasQuestion: true,
        creationDate: demoDate(4, 10),
        sentDate: demoDate(4, 10, 9),
        emailLinkViewedAt: demoDate(4, 12, 10),
        dueDate: demoDate(3, 8),
        recipient: DEMO_RECIPIENTS[1],
        patient: DEMO_PATIENTS[1],
        source: "pad",
    },
    {
        // NOT SENT — P≠R (Meier → Anna)
        id: "demo-inv-9",
        readableId: "RE-2026-0009",
        status: 0,
        amount: 149.90,
        paidAmount: 0,
        dunning: 0,
        creationDate: demoDate(0, 5),
        recipient: DEMO_RECIPIENTS[3],
        patient: DEMO_PATIENTS[4],
        source: "pad",
    },
    {
        // NOT SENT — P=R (König, incomplete)
        id: "demo-inv-10",
        readableId: "RE-2026-0010",
        status: 0,
        amount: 289.00,
        paidAmount: 0,
        dunning: 0,
        creationDate: demoDate(0, 8),
        recipient: DEMO_RECIPIENTS[4],
        patient: DEMO_PATIENTS[5],
        source: "pad",
    },
];

// ── Exams (for invoice detail) ────────────────────────────────────────────────

export const DEMO_EXAMS: Record<string, Exam[]> = {
    "demo-inv-1": [
        { id: "e1", code: "1",    description: "Beratung",                    date: demoDate(3, 4), quantity: 1, price: 10.72, factor: 2.3,  materialCosts: 0 },
        { id: "e2", code: "5",    description: "Symptombezogene Untersuchung", date: demoDate(3, 4), quantity: 1, price: 9.27,  factor: 2.3,  materialCosts: 0 },
        { id: "e3", code: "250",  description: "Blutabnahme",                  date: demoDate(3, 4), quantity: 1, price: 2.33,  factor: 1.8,  materialCosts: 0 },
        { id: "e4", code: "3550", description: "Kleines Blutbild",             date: demoDate(3, 4), quantity: 1, price: 5.83,  factor: 1.15, materialCosts: 0 },
    ],
    "demo-inv-2": [
        { id: "e5", code: "1",   description: "Beratung",                date: demoDate(3, 11), quantity: 1, price: 10.72, factor: 3.5, materialCosts: 0 },
        { id: "e6", code: "6",   description: "Vollständige Untersuchung", date: demoDate(3, 11), quantity: 1, price: 17.49, factor: 2.3, materialCosts: 0 },
        { id: "e7", code: "410", description: "EKG",                      date: demoDate(3, 11), quantity: 1, price: 12.77, factor: 1.8, materialCosts: 0 },
    ],
    "demo-inv-8": [
        { id: "e8", code: "1",   description: "Beratung",                    date: demoDate(4, 9), quantity: 1, price: 10.72, factor: 2.3, materialCosts: 0 },
        { id: "e9", code: "34",  description: "Erörterung Untersuchungsergebnis", date: demoDate(4, 9), quantity: 1, price: 17.49, factor: 2.3, materialCosts: 0 },
        { id: "e10", code: "375", description: "Infusion", date: demoDate(4, 9), quantity: 2, price: 8.45, factor: 1.8, materialCosts: 5.00 },
    ],
};

// ── Bank transactions (6) ─────────────────────────────────────────────────────
// 3 auto-matched (inv-1,2,3 → paid)
// 2 wrong reference (inv-4,7 → need manual match)
// 1 nonsense (Metro/Berufsverband → to ignore)

export const DEMO_BANK_STATUS: FinApiStatus = {
    connected: true,
    bankName: "Commerzbank",
    accounts: [
        {
            id: 1,
            accountName: "Geschäftskonto Praxis Dr. Müller",
            iban: "DE89370400440532013000",
            accountType: "Checking",
            balance: 12847.30,
            currency: "EUR",
        },
    ],
    lastSyncAt: demoDate(0, 1),
    recentTransactions: [
        {
            id: "tx-1",
            amount: 485.50,
            currency: "EUR",
            purpose: "RE-2026-0001 Wagner Heinrich Arztrechnung",
            counterpartName: "Prof. Dr. Heinrich Wagner",
            bookingDate: demoDate(2, 21),
            matchedInvoiceIds: ["demo-inv-1"],
            matchedInvoices: [{ id: "demo-inv-1", readableId: "RE-2026-0001", patientName: "Heinrich Wagner" }],
            matchedBy: "auto",
            ignored: false,
            ownerDoctorId: "demo-doctor-1",
            ownerDoctorName: "Dr. Müller",
        },
        {
            id: "tx-2",
            amount: 312.80,
            currency: "EUR",
            purpose: "RE-2026-0002 Schmidt Maximilian Praxis Müller",
            counterpartName: "Brigitte Schmidt",
            bookingDate: demoDate(2, 16),
            matchedInvoiceIds: ["demo-inv-2"],
            matchedInvoices: [{ id: "demo-inv-2", readableId: "RE-2026-0002", patientName: "Maximilian Schmidt" }],
            matchedBy: "auto",
            ignored: false,
            ownerDoctorId: "demo-doctor-1",
            ownerDoctorName: "Dr. Müller",
        },
        {
            id: "tx-3",
            amount: 198.00,
            currency: "EUR",
            purpose: "RE-2026-0003 Hoffmann Maria",
            counterpartName: "Maria Hoffmann",
            bookingDate: demoDate(2, 11),
            matchedInvoiceIds: ["demo-inv-3"],
            matchedInvoices: [{ id: "demo-inv-3", readableId: "RE-2026-0003", patientName: "Maria Hoffmann" }],
            matchedBy: "auto",
            ignored: false,
            ownerDoctorId: "demo-doctor-1",
            ownerDoctorName: "Dr. Müller",
        },
        {
            id: "tx-4",
            amount: 754.20,
            currency: "EUR",
            purpose: "RE20260004 Meier Familie Arztpraxis",
            counterpartName: "Dr. Thomas Meier",
            bookingDate: demoDate(1, 10),
            matchedInvoiceIds: [],
            matchedInvoices: [],
            matchedBy: null,
            ignored: false,
            ownerDoctorId: "demo-doctor-1",
            ownerDoctorName: "Dr. Müller",
        },
        {
            id: "tx-5",
            amount: 445.60,
            currency: "EUR",
            purpose: "KTO Weiterleitung Wagner RE20260007 Praxis",
            counterpartName: "H. Wagner",
            bookingDate: demoDate(1, 14),
            matchedInvoiceIds: [],
            matchedInvoices: [],
            matchedBy: null,
            ignored: false,
            ownerDoctorId: "demo-doctor-1",
            ownerDoctorName: "Dr. Müller",
        },
        {
            id: "tx-6",
            amount: 67.80,
            currency: "EUR",
            purpose: "METRO AG Mitgliedschaft Berufsverband Ärzte",
            counterpartName: "METRO AG",
            bookingDate: demoDate(0, 3),
            matchedInvoiceIds: [],
            matchedInvoices: [],
            matchedBy: null,
            ignored: false,
            ownerDoctorId: "demo-doctor-1",
            ownerDoctorName: "Dr. Müller",
        },
    ],
    pendingTransactions: [],
};

// ── Dashboard activity (5 static) ─────────────────────────────────────────────

export const DEMO_DASHBOARD_ACTIVITY = {
    items: [
        {
            type: "bankTransactionMatched",
            level: "info",
            date: demoDate(2, 21, 9),
            invoiceId: "demo-inv-1",
            readableId: "RE-2026-0001",
            recipientName: "Prof. Dr. Wagner",
        },
        {
            type: "dunningAdvanced",
            level: "warning",
            date: demoDate(1, 20, 8),
            invoiceId: "demo-inv-8",
            readableId: "RE-2026-0008",
            dunningLevel: 3,
            recipientName: "B. Schmidt",
        },
        {
            type: "smsOtpUsed",
            level: "info",
            date: demoDate(1, 15, 10),
            invoiceId: "demo-inv-6",
            readableId: "RE-2026-0006",
            recipientName: "H.-J. Bauer",
        },
        {
            type: "questionnaireFilled",
            level: "info",
            date: demoDate(1, 8, 11),
            recipientId: "demo-rec-2",
            recipientName: "Brigitte Schmidt",
        },
        {
            type: "smsOtpUsed",
            level: "info",
            date: demoDate(0, 3, 9),
            invoiceId: "demo-inv-5",
            readableId: "RE-2026-0005",
            recipientName: "P. König",
        },
    ],
    lookbackDays: 90,
};

// ── Demo question (for invoice RE-2026-0008) ──────────────────────────────────

export const DEMO_QUESTION = {
    id: "demo-q-1",
    invoiceId: "demo-inv-8",
    readableId: "RE-2026-0008",
    recipientId: "demo-rec-2",
    recipientName: "Brigitte Schmidt",
    patientName: "Maximilian Schmidt",
    question: "Guten Tag, ich habe die Rechnung erhalten und bin mit dem Betrag nicht einverstanden. Können Sie mir bitte die einzelnen Leistungen nochmals erklären?",
    createdAt: demoDate(4, 15, 14),
    replies: [],
    resolved: false,
};

// ── Open invoices for manual bank match popup ──────────────────────────────────

export const DEMO_OPEN_INVOICES = DEMO_INVOICES.filter(
    inv => inv.status !== 2 && inv.status !== 5 && inv.status !== 6 && inv.sentDate,
);

// ── Quota ─────────────────────────────────────────────────────────────────────

export const DEMO_QUOTA = {
    invoiceCount: 10,
    invoiceAllowance: null,
    overageRate: 0,
};

// ── Settings ──────────────────────────────────────────────────────────────────

export const DEMO_SETTINGS = {
    sendMethods: ["email", "post"],
    requireLegalAcceptance: false,
    questionsEnabled: true,
    sendOnUpload: false,
    emailLinkModes: ["login"],
    postColor: false,
    postDuplex: true,
    postDispatch: "STANDARD",
    postEnvelope: "C4",
    postShipping: "PRIORITY",
    paidThreshold: 2,
    legalMode: "api",
};
